/**
 * Shell applicatif : en-tête sticky compact (titre, sélecteur de mode, toggle
 * thème, fraîcheur des données), bandeau dégradé, conteneurs des deux modes.
 */
import { toggleTheme } from "../app/theme.ts";
import { fmtAgo, fmtShortDateTime, freshness } from "../app/format.ts";
import { t, lang, setLang, languages, type Lang } from "../app/i18n.ts";
import type { LiveUpdate } from "../app/live.ts";

export type Mode = "dashboard" | "projection" | "sim" | "stats" | "method";

// Emblème ORIGINAL (PAS le logo FIFA ni l'emblème/ballon officiel 2026, protégés) :
// un ballon à structure classique (pentagones), aux panneaux colorés — INSPIRÉ d'une
// palette vive (couleurs des pays hôtes bleu/rouge/vert + l'or du site), pas une copie.
// Les drapeaux des 3 pays hôtes (USA/CAN/MEX) ne sont pas protégés non plus.
const EMBLEM = `<svg viewBox="0 0 40 40" class="sh-emb-svg" aria-hidden="true" focusable="false">
  <defs>
    <polygon id="cbp" points="0,-1 0.951,-0.309 0.588,0.809 -0.588,0.809 -0.951,-0.309" />
    <clipPath id="cbc"><circle cx="20" cy="20" r="17.6" /></clipPath>
  </defs>
  <circle cx="20" cy="20" r="18.4" fill="#f6f5f1" stroke="var(--color-text-warning)" stroke-width="2" />
  <g clip-path="url(#cbc)" stroke="#15140f" stroke-width="0.9" stroke-linejoin="round">
    <use href="#cbp" transform="translate(20 20) scale(6.4)" fill="#1f6fe0" />
    <use href="#cbp" transform="translate(28.7 8.03) rotate(36) scale(4.3)" fill="#f2a72a" />
    <use href="#cbp" transform="translate(34.08 24.57) rotate(108) scale(4.3)" fill="#e0322f" />
    <use href="#cbp" transform="translate(20 34.8) rotate(180) scale(4.3)" fill="#1f9d63" />
    <use href="#cbp" transform="translate(5.92 24.57) rotate(252) scale(4.3)" fill="#15a8bd" />
    <use href="#cbp" transform="translate(11.3 8.03) rotate(324) scale(4.3)" fill="#d6457f" />
  </g>
</svg>`;

export interface ShellRefs {
  readonly dashboard: HTMLElement;
  readonly projection: HTMLElement;
  readonly sim: HTMLElement;
  readonly stats: HTMLElement;
  readonly method: HTMLElement;
  setMode(mode: Mode): void;
  onModeChange(cb: (mode: Mode) => void): void;
  renderFreshness(update: LiveUpdate, now: number): void;
  onRefreshClick(cb: () => void): void;
  onReplayClick(cb: () => void): void;
}

export function modeFromHash(): Mode {
  if (location.hash.startsWith("#/projection")) return "projection";
  if (location.hash.startsWith("#/sim")) return "sim";
  if (location.hash.startsWith("#/stats")) return "stats";
  if (location.hash.startsWith("#/method")) return "method";
  return "dashboard";
}

export function mountShell(root: HTMLElement): ShellRefs {
  const langOpts = languages()
    .map(
      (l) => `<option value="${l}"${l === lang() ? " selected" : ""}>${l.toUpperCase()}</option>`,
    )
    .join("");
  root.innerHTML = `
    <div id="stickyWrap">
      <header class="stage shell-head">
        <div class="sh-row">
          <a class="sh-brand" href="#/" aria-label="${t("brand.aria")}">
            <span class="sh-emblem">${EMBLEM}</span>
            <span class="sh-word">
              <h1 class="sh-title"><span class="sh-w-name">MONDIAL</span> <span class="sh-w-year">2026</span></h1>
              <span class="sh-hosts" title="${t("brand.hosts")}" aria-hidden="true"><span class="fi fi-us"></span><span class="fi fi-ca"></span><span class="fi fi-mx"></span></span>
            </span>
          </a>
          <nav class="modes" aria-label="${t("mode.aria")}">
            <a href="#/" data-mode="dashboard" class="mode-tab">${t("mode.dashboard")}</a>
            <a href="#/projection" data-mode="projection" class="mode-tab">${t("mode.projection")}</a>
            <a href="#/sim" data-mode="sim" class="mode-tab">${t("mode.sim")}</a>
            <a href="#/stats" data-mode="stats" class="mode-tab">${t("mode.stats")}</a>
            <a href="#/method" data-mode="method" class="mode-tab mode-tab-method" title="${t("mode.method.aria")}"><i class="ti ti-help-circle" aria-hidden="true"></i> ${t("mode.method")}</a>
          </nav>
          <div class="sh-actions">
            <button id="bReplay" title="${t("replay.title")}"><i class="ti ti-player-play" aria-hidden="true" style="vertical-align:-2px"></i> ${t("replay")}</button>
            <select id="bLang" class="sh-lang" aria-label="${t("lang.aria")}" title="${t("lang.aria")}">${langOpts}</select>
            <button id="bTheme" aria-label="${t("theme.aria")}" title="${t("theme.title")}"><i id="thIcn" class="ti ti-sun" aria-hidden="true"></i></button>
          </div>
        </div>
        <div class="sh-fresh" id="freshRow" aria-live="polite" aria-atomic="true">
          <span class="fresh-dot" id="freshDot"></span>
          <span id="freshLabel">${t("fresh.connecting")}</span>
          <button id="bRefresh" class="linklike" title="${t("refresh.title")}"><i class="ti ti-refresh" aria-hidden="true"></i></button>
        </div>
        <div class="sh-degraded hide" id="degradedBanner" role="status"></div>
      </header>
    </div>
    <main class="wrap">
      <section id="view-dashboard"></section>
      <section id="view-projection" class="hide"></section>
      <section id="view-sim" class="hide"></section>
      <section id="view-stats" class="hide"></section>
      <section id="view-method" class="hide"></section>
    </main>
    <div class="rotate-hint" role="status">
      <span class="rotate-hint-ico" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="7" y="2.5" width="10" height="19" rx="2.2"></rect><line x1="10.5" y1="18.5" x2="13.5" y2="18.5"></line></svg></span>
      <span class="rotate-hint-txt">${t("rotate.hint")}</span>
      <button class="rotate-hint-x" type="button" aria-label="${t("rotate.dismiss")}"><i class="ti ti-x" aria-hidden="true"></i></button>
    </div>
  `;

  const themeBtn = root.querySelector<HTMLButtonElement>("#bTheme");
  const themeIcon = root.querySelector<HTMLElement>("#thIcn");
  const syncThemeIcon = (): void => {
    if (themeIcon)
      themeIcon.className = `ti ${document.documentElement.dataset.theme === "dark" ? "ti-sun" : "ti-moon"}`;
  };
  if (themeBtn) {
    themeBtn.onclick = () => {
      toggleTheme();
      syncThemeIcon();
    };
  }
  syncThemeIcon();
  // Sélecteur de langue : le choix manuel est mémorisé et recharge la page.
  const langSel = root.querySelector<HTMLSelectElement>("#bLang");
  if (langSel) langSel.onchange = () => setLang(langSel.value as Lang);

  // Conseil « passez en portrait » (téléphone en paysage) : la visibilité est pilotée
  // par media query ; ici on gère seulement le rejet, mémorisé pour la session.
  const rotateHint = root.querySelector<HTMLElement>(".rotate-hint");
  if (rotateHint) {
    try {
      if (sessionStorage.getItem("wc26-rotateHint") === "off") rotateHint.classList.add("is-dismissed");
    } catch {
      /* sessionStorage indispo (mode privé strict) → le conseil reste affichable */
    }
    rotateHint.querySelector(".rotate-hint-x")?.addEventListener("click", () => {
      rotateHint.classList.add("is-dismissed");
      try {
        sessionStorage.setItem("wc26-rotateHint", "off");
      } catch {
        /* ignore */
      }
    });
  }

  const dashboard = root.querySelector<HTMLElement>("#view-dashboard");
  const sim = root.querySelector<HTMLElement>("#view-sim");
  const projection = root.querySelector<HTMLElement>("#view-projection");
  const stats = root.querySelector<HTMLElement>("#view-stats");
  const method = root.querySelector<HTMLElement>("#view-method");
  const dot = root.querySelector<HTMLElement>("#freshDot");
  const label = root.querySelector<HTMLElement>("#freshLabel");
  const banner = root.querySelector<HTMLElement>("#degradedBanner");
  const refreshBtn = root.querySelector<HTMLButtonElement>("#bRefresh");
  const replayBtn = root.querySelector<HTMLButtonElement>("#bReplay");
  if (!dashboard || !sim || !projection || !stats || !method || !dot || !label || !banner)
    throw new Error("shell incomplet");

  const modeListeners: Array<(mode: Mode) => void> = [];
  const setMode = (mode: Mode): void => {
    dashboard.classList.toggle("hide", mode !== "dashboard");
    sim.classList.toggle("hide", mode !== "sim");
    projection.classList.toggle("hide", mode !== "projection");
    stats.classList.toggle("hide", mode !== "stats");
    method.classList.toggle("hide", mode !== "method");
    // Le bouton « Rejouer » du header ne concerne que le dashboard ; la
    // Simulation a ses propres contrôles.
    if (replayBtn) replayBtn.classList.toggle("hide", mode !== "dashboard");
    root.querySelectorAll<HTMLElement>(".mode-tab").forEach((tab) => {
      const active = tab.dataset.mode === mode;
      tab.classList.toggle("on", active);
      if (active) tab.setAttribute("aria-current", "page");
      else tab.removeAttribute("aria-current");
    });
    for (const cb of modeListeners) cb(mode);
  };

  const renderFreshness = (update: LiveUpdate, now: number): void => {
    const generatedAt = update.data?.generatedAt;
    if (!generatedAt) {
      dot.className = "fresh-dot dead";
      const txt = t(update.degraded ? "fresh.unavailable" : "fresh.connecting");
      dot.title = txt;
      dot.setAttribute("aria-label", txt);
      label.textContent = txt;
    } else {
      const age = now - Date.parse(generatedAt);
      const level = update.degraded ? "dead" : freshness(age);
      dot.className = `fresh-dot ${level}`;
      // l'état n'est plus porté QUE par la couleur (WCAG 1.4.1) : mot + title.
      const stateWord = t(
        level === "fresh" ? "fresh.up" : level === "stale" ? "fresh.late" : "fresh.offline",
      );
      dot.title = stateWord;
      dot.setAttribute("aria-label", t("fresh.state", { state: stateWord }));
      label.textContent = `${stateWord} · ${fmtAgo(age)}`;
    }
    const showBanner = update.degraded || update.fromCache;
    banner.classList.toggle("hide", !showBanner);
    if (showBanner) {
      banner.textContent = generatedAt
        ? t("banner.cached", { date: fmtShortDateTime(generatedAt) })
        : t("banner.degraded");
    }
  };

  return {
    dashboard,
    sim,
    projection,
    stats,
    method,
    setMode,
    onModeChange(cb) {
      modeListeners.push(cb);
    },
    renderFreshness,
    onRefreshClick(cb) {
      if (refreshBtn) refreshBtn.onclick = cb;
    },
    onReplayClick(cb) {
      if (replayBtn) replayBtn.onclick = cb;
    },
  };
}
